import i18n from '../i18n'
import { errorCode } from '../services/api'
import { faDigits } from './numerals'

/**
 * The shop's art by catalogue id (design page 12, `shop`), mirrored from the
 * Android app's `ShopLogic.kt` so both clients draw the same nine avatars
 * and four packs for the same ids, and say the same sentence for the same
 * answer. The wallet, the prices and every purchase rule are the server's
 * (B5); the prices here are the design's and only a fallback for the
 * moment before `shop_catalog` arrives. This is a plain lib file, not a
 * component, so it reads `i18n.t()` directly — the `shop` namespace — and
 * text fields that are read off module-level data (avatar/pack names,
 * rarity labels) are getters so they stay current across a language switch.
 */

const t = (key, options) => i18n.t(key, { ns: 'shop', ...options })

export const FREE_AVATAR = 'classic'

/** The design's exchange rate, as the convert row states it; the server's `streakToCoins` is the one that applies. */
export const STREAK_TO_COINS = 200

export const RARITY_LABEL = {
  get base() {
    return t('rarity.base')
  },
  get common() {
    return t('rarity.common')
  },
  get rare() {
    return t('rarity.rare')
  },
  get epic() {
    return t('rarity.epic')
  },
  get animated() {
    return t('rarity.animated')
  },
  get legendary() {
    return t('rarity.legendary')
  },
}

/** The rarity badge's tint. */
export const RARITY_TILE = {
  base: 'bg-surface-2 text-ink-2',
  common: 'bg-good-tint text-good-ink',
  rare: 'bg-brand-50 text-brand-700',
  epic: 'bg-warn-tint text-warn-ink',
  animated: 'bg-brand-600 text-white dark:text-slate-900',
  legendary: 'bg-accent text-on-accent',
}

/**
 * The disc and ring an avatar sits on. The parrot mark itself is never
 * tinted (design-system.md), so avatars differ by what is around the
 * bird, not the bird.
 */
export const TONE = {
  brand: { disc: 'bg-brand-50', ring: 'border-brand-600' },
  good: { disc: 'bg-good-tint', ring: 'border-good' },
  accent: { disc: 'bg-accent-tint', ring: 'border-accent' },
  bad: { disc: 'bg-bad-tint', ring: 'border-bad' },
  night: { disc: 'bg-surface-3', ring: 'border-brand-800' },
  legend: { disc: 'bg-accent-tint', ring: 'border-accent-ink' },
}

export const AVATARS = [
  { id: 'classic', get name() { return t('avatars.classic.name') }, rarity: 'base', price: 0, currency: 'coin', tone: 'brand' },
  { id: 'mint', get name() { return t('avatars.mint.name') }, rarity: 'common', price: 300, currency: 'coin', tone: 'good' },
  { id: 'gold', get name() { return t('avatars.gold.name') }, rarity: 'rare', price: 450, currency: 'coin', tone: 'accent' },
  { id: 'sunset', get name() { return t('avatars.sunset.name') }, rarity: 'rare', price: 600, currency: 'coin', tone: 'bad' },
  { id: 'night', get name() { return t('avatars.night.name') }, rarity: 'epic', price: 7, currency: 'streak', tone: 'night' },
  { id: 'aurora', get name() { return t('avatars.aurora.name') }, rarity: 'animated', price: 1200, currency: 'coin', tone: 'brand', animated: true },
  { id: 'ember', get name() { return t('avatars.ember.name') }, rarity: 'animated', price: 1800, currency: 'coin', tone: 'bad', animated: true },
  { id: 'mythic', get name() { return t('avatars.mythic.name') }, rarity: 'animated', price: 21, currency: 'streak', tone: 'accent', animated: true },
  { id: 'legend', get name() { return t('avatars.legend.name') }, rarity: 'legendary', price: 14, currency: 'streak', tone: 'legend' },
]

export const PACKS = [
  {
    id: 'shield1',
    get name() { return t('packs.shield1.name') },
    get badge() { return t('packs.shield1.badge') },
    get description() { return t('packs.shield1.description') },
    price: 250,
    currency: 'coin',
    icon: 'shield',
  },
  {
    id: 'shield3',
    get name() { return t('packs.shield3.name') },
    get badge() { return t('packs.shield3.badge') },
    get description() { return t('packs.shield3.description') },
    price: 650,
    currency: 'coin',
    icon: 'shield',
    popular: true,
  },
  {
    id: 'repair',
    get name() { return t('packs.repair.name') },
    get badge() { return t('packs.repair.badge') },
    get description() { return t('packs.repair.description') },
    price: 900,
    currency: 'coin',
    icon: 'flame',
  },
  {
    id: 'freeze7',
    get name() { return t('packs.freeze7.name') },
    get badge() { return t('packs.freeze7.badge') },
    get description() { return t('packs.freeze7.description') },
    price: 5,
    currency: 'streak',
    icon: 'bolt',
  },
]

/**
 * «فریز» (V6-b-gem): one streak freeze for gems. The server lists it — and
 * sells it — only while its gem economy is on (`gemsEnabled`), when it
 * replaces the coin shield packs and `freeze7`. Its price here is only the
 * fallback before `shop_catalog` arrives; the catalogue's price applies.
 */
export const FREEZE_PACK = {
  id: 'freeze',
  get name() { return t('packs.freeze.name') },
  get badge() { return t('packs.freeze.badge') },
  get description() { return t('packs.freeze.description') },
  price: 5,
  currency: 'gem',
  icon: 'shield',
}

/**
 * Whether the server runs the gem economy (V6-b-gem, AppSetting
 * `gems.enabled`). Only an explicit `true` counts: a wallet from a server
 * before V6-b-gem, or with the switch off (production until stage 3), keeps
 * today's coin shop exactly as it was.
 */
export function gemsOn(wallet) {
  return wallet?.gemsEnabled === true
}

/**
 * The packs to draw. Gems off: today's four coin/streak packs, untouched.
 * Gems on: the catalogue's packs in its order (the freeze and the repair),
 * with this file's art for each id it knows; before the catalogue arrives,
 * the freeze and the repair.
 */
export function visiblePacks(catalog, gems) {
  if (!gems) return PACKS
  const art = [FREEZE_PACK, ...PACKS]
  const ids = Array.isArray(catalog?.packs) && catalog.packs.length > 0 ? catalog.packs.map((pack) => pack.id) : ['freeze', 'repair']
  return ids.map((id) => art.find((pack) => pack.id === id)).filter(Boolean)
}

/**
 * The freeze counter `x/max` (V6-b-gem): null while there is no cap (gems
 * off, `shieldsMax` null), otherwise the shields held, the cap, and whether
 * the wallet is full (a fourth freeze is refused by the server as
 * `freeze_limit`).
 */
export function freezeState(wallet) {
  const max = wallet?.shieldsMax
  if (!gemsOn(wallet) || typeof max !== 'number' || max <= 0) return null
  const held = Math.max(0, wallet?.shields ?? 0)
  return { held, max, full: held >= max }
}

/** A badge's name in the app's language: the English string by key, else the server's Persian name. */
export function badgeName(badge) {
  const key = `badges.${badge?.key}.name`
  const english = i18n.language === 'en' ? t(key, { defaultValue: '' }) : ''
  if (english) return english
  return badge?.nameFa || t(key, { defaultValue: badge?.key ?? '' })
}

/** An unknown id draws as the free avatar rather than as nothing. */
export function avatarById(id) {
  return AVATARS.find((avatar) => avatar.id === id) ?? AVATARS[0]
}

/** The server's price for an item, or the design's while the catalogue has not arrived. */
export function priceOf(catalog, item) {
  const rows = catalog ? [...(catalog.avatars ?? []), ...(catalog.packs ?? [])] : []
  const row = rows.find((candidate) => candidate.id === item.id)
  const price = row ? row.price : item.price
  const currency = String(row ? row.currency : item.currency).toLowerCase()
  return { price, streakPriced: currency === 'streak', gemPriced: currency === 'gem' }
}

/** The free avatar is always owned; the rest as the wallet says. */
export function owns(wallet, avatarId) {
  return avatarId === FREE_AVATAR || (wallet?.owned ?? []).includes(avatarId)
}

/** The balance an item spends from: the live streak for streak-priced items, gems for gem-priced ones (V6-b-gem), coins otherwise. */
export function canAfford(wallet, { price, streakPriced, gemPriced }) {
  const balance = streakPriced ? (wallet?.streakDays ?? 0) : gemPriced ? (wallet?.gems ?? 0) : (wallet?.coins ?? 0)
  return balance >= price
}

/** The sentence for what the server did. A name this build does not know is still done. */
export function messageFor(outcome, name = '') {
  switch (outcome) {
    case 'selected':
      return { good: true, text: t('message.selected', { name }) }
    case 'unlocked':
      return { good: true, text: t('message.unlocked', { name }) }
    case 'activated':
      return { good: true, text: t('message.activated', { name }) }
    case 'converted':
      return { good: true, text: t('message.converted', { coins: faDigits(STREAK_TO_COINS) }) }
    default:
      return { good: true, text: t('message.default') }
  }
}

/**
 * The sentence for why the server refused, read by what was tapped
 * (`avatar` · `pack` · `convert`): the same refusal reads differently
 * under an avatar, a pack, and the convert button. A request that never
 * reached the server is offline, not a refusal.
 */
export function refusalFor(error, kind) {
  if (!error?.response) {
    return { good: false, text: t('refusal.offline') }
  }
  switch (errorCode(error)) {
    case 'insufficient_coins':
      return { good: false, text: kind === 'avatar' ? t('refusal.insufficientCoinsAvatar') : t('refusal.insufficientBalance') }
    case 'insufficient_streak':
      if (kind === 'avatar') return { good: false, text: t('refusal.insufficientStreakAvatar') }
      if (kind === 'convert') return { good: false, text: t('refusal.insufficientStreakConvert') }
      return { good: false, text: t('refusal.insufficientBalance') }
    case 'not_owned':
      return { good: false, text: t('refusal.notOwned') }
    // V6-b-gem, only while the server's gem economy is on.
    case 'insufficient_gems':
      return { good: false, text: kind === 'avatar' ? t('refusal.insufficientGemsAvatar') : t('refusal.insufficientGems') }
    case 'freeze_limit':
      return { good: false, text: t('refusal.freezeLimit') }
    case 'item_retired':
      return { good: false, text: t('refusal.itemRetired') }
    case 'coins_retired':
      return { good: false, text: t('refusal.coinsRetired') }
    default:
      return { good: false, text: t('refusal.default') }
  }
}
